import type { CSSProperties } from 'react';
import { cn } from '@unitfield/ui/lib/utils';
import styles from './public-loading.module.css';

/**
 * The loader of an organization's own pages: a plain ring on the page's ink, never the Unitfield mark.
 * Public pages (the storefront's apply flow, sign-in, listing and status pages) run on a business's host,
 * so every wait there uses this; Unitfield's own surfaces keep the Unitfield loader.
 */
export function PublicSpinner({
  size = 28,
  label = 'Loading',
  decorative = false,
  className,
}: {
  size?: number;
  label?: string;
  /** Inside a control that already says what is happening (an upload tile): no status of its own. */
  decorative?: boolean;
  className?: string;
}) {
  return (
    <span
      role={decorative ? undefined : 'status'}
      aria-label={decorative ? undefined : label}
      aria-hidden={decorative ? true : undefined}
      data-public-spinner=""
      className={cn(styles.spinner, className)}
      style={{ '--public-spinner-size': `${size}px` } as CSSProperties}
    >
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
        <circle className={styles.track} cx="12" cy="12" r="9" />
        <circle className={styles.arc} cx="12" cy="12" r="9" pathLength="100" />
      </svg>
    </span>
  );
}

/** A whole page or section waiting for its content: the ring, centred in the room the content will take. */
export function PublicLoading({ label = 'Loading', className }: { label?: string; className?: string }) {
  return (
    <div data-loading-region="" aria-busy="true" className={cn(styles.region, className)}>
      <PublicSpinner size={36} label={label} />
    </div>
  );
}
